/**
 * Lista ÚNICA de domínios da plataforma (plano pratzu-dominio, aprovado).
 *
 * - CANONICAL_DOMAIN: pratzu.com — domínio canônico (links, e-mails, defaults).
 * - SERVED_DOMAINS: domínios que respondem de verdade (API, menus, admin,
 *   landing). asknamba.com segue em paralelo durante a transição;
 *   self-restaurant.app é o legado dos QR codes impressos (menus/admin).
 * - REDIRECT_DOMAINS: só redirecionam (301 → mesmo subdomínio em pratzu.com,
 *   path + query preservados). NUNCA são origem CORS/return_to/cookie.
 *
 * A API nunca é redirecionada (webhooks/POST/OAuth callback não seguem 301).
 * Sem dependências: importável pelo Worker, Astro, Vite e landing.
 *
 * Ambientes efémeros `pr-N` (#435): lógica em `pr-env.ts`, re-exportada abaixo.
 */
import { prEnvApiUrl, PR_ENV_ORIGIN_PATTERNS } from './pr-env.js'
export {
  PR_ENV_HOST_RE,
  PR_ENV_ORIGIN_PATTERNS,
  prEnvApiUrl,
  prEnvOf,
  type PrEnvApp,
} from './pr-env.js'

export const CANONICAL_DOMAIN = 'pratzu.com'
export const TRANSITION_DOMAINS = ['asknamba.com'] as const
export const LEGACY_DOMAINS = ['self-restaurant.app'] as const
export const SERVED_DOMAINS = [CANONICAL_DOMAIN, ...TRANSITION_DOMAINS, ...LEGACY_DOMAINS] as const
/** Domínios que recebem o cookie de idioma compartilhado (domínio pai). */
export const COOKIE_DOMAINS = [CANONICAL_DOMAIN, ...TRANSITION_DOMAINS] as const
export const REDIRECT_DOMAINS = ['pratzu.app'] as const

/**
 * Subdomínio do painel (admin) por domínio: em pratzu.com o painel é
 * `console.` (fase 2 da migração); nos domínios antigos segue `admin.`.
 * admin.asknamba.com continua servindo em paralelo (redirect só na fase 3).
 */
export function adminSubdomainFor(domain: string): 'console' | 'admin' {
  return domain === CANONICAL_DOMAIN ? 'console' : 'admin'
}

/**
 * Domínio em que a PRODUÇÃO está publicada — base dos defaults de build
 * (URL da API, login do painel, domínio dos menus). Fase 2: pratzu.com
 * (api.pratzu.com, console.pratzu.com, *.menu.pratzu.com); asknamba.com
 * continua respondendo em paralelo (CORS/CSP/cookies/return_to aceitam os dois).
 */
export const LIVE_PRODUCTION_DOMAIN: string = 'pratzu.com'

export const LIVE_PRODUCTION_URLS = {
  api: `https://api.${LIVE_PRODUCTION_DOMAIN}`,
  apiWs: `wss://api.${LIVE_PRODUCTION_DOMAIN}`,
  admin: `https://${adminSubdomainFor(LIVE_PRODUCTION_DOMAIN)}.${LIVE_PRODUCTION_DOMAIN}`,
  // Menus por restaurante em pratzu.com/r/<slug> (#285): sem ACM, sem curinga
  // de dois níveis. Mesmo valor do CF_MENU_DOMAIN de produção (base com path).
  menuDomain: `${LIVE_PRODUCTION_DOMAIN}/r`,
} as const

/** Hosts canônicos (produção). plate.asknamba.com virou a raiz pratzu.com. */
export const CANONICAL_HOSTS = {
  landing: CANONICAL_DOMAIN,
  www: `www.${CANONICAL_DOMAIN}`,
  api: `api.${CANONICAL_DOMAIN}`,
  admin: `console.${CANONICAL_DOMAIN}`,
  menu: `menu.${CANONICAL_DOMAIN}`,
} as const

/** Hosts de staging no domínio canônico. */
export const STAGING_HOSTS = {
  api: `api-staging.${CANONICAL_DOMAIN}`,
  admin: `console-staging.${CANONICAL_DOMAIN}`,
  menu: `menu-staging.${CANONICAL_DOMAIN}`,
  landing: `landing-staging.${CANONICAL_DOMAIN}`,
} as const

export const CANONICAL_URLS = {
  landing: `https://${CANONICAL_HOSTS.landing}`,
  api: `https://${CANONICAL_HOSTS.api}`,
  apiWs: `wss://${CANONICAL_HOSTS.api}`,
  admin: `https://${CANONICAL_HOSTS.admin}`,
} as const

const esc = (d: string) => d.replace(/[.]/g, '\\.')

/** Domínio servido ao qual o host pertence (apex ou subdomínio), ou null. */
export function servedDomainOf(host: string): (typeof SERVED_DOMAINS)[number] | null {
  const h = host.toLowerCase()
  return SERVED_DOMAINS.find((d) => h === d || h.endsWith(`.${d}`)) ?? null
}

/**
 * Domínio pai (pratzu.com / asknamba.com) ao qual o host pertence, sem a
 * regra host-only de staging. Usado por `sameSiteApiUrl` (troca de API na
 * transição); cookies de idioma usam `sharedCookieDomain`.
 */
function cookieParentDomain(host: string): string | undefined {
  const h = host.toLowerCase()
  return COOKIE_DOMAINS.find((d) => h === d || h.endsWith(`.${d}`))
}

/**
 * Domínio pai para o cookie de idioma cross-app (pratzu.com ou asknamba.com),
 * ou undefined (host-only: localhost, pages.dev, legado, redirect, staging).
 *
 * #435 / D2: o primeiro rótulo com `staging` (ex.: `console-staging`,
 * `pr-12-landing-staging`) → host-only, para não colidir cookies entre
 * pr-N, staging partilhado e produção no mesmo domínio pai. Produção
 * (sem `staging` no primeiro rótulo) mantém o Domain= do domínio pai.
 */
export function sharedCookieDomain(host: string): string | undefined {
  const h = host.toLowerCase()
  const firstLabel = h.split('.')[0] ?? ''
  if (/(^|-)staging(-|$)/.test(firstLabel)) return undefined
  return cookieParentDomain(h)
}

// ─── Alvo da API do proxy do admin (pr-N via pr-env; staging / produção) ─────

/** API de produção usada pelo proxy do admin (Pages Functions). */
export const ADMIN_API_URL_PRODUCTION =
  'https://self-restaurant-api-production.eduardo-044.workers.dev'

/** API do staging partilhado, alinhada ao domínio do painel. */
export function sharedStagingApiUrl(host: string): string {
  const domain = servedDomainOf(host)
  return `https://api-staging.${domain && domain !== 'self-restaurant.app' ? domain : 'pratzu.com'}`
}

/**
 * Alvo da API para o proxy do admin (`packages/admin/functions`).
 * Ordem: pr-N válido (`prEnvApiUrl`) → staging partilhado (substring
 * `staging`, comportamento pré-#435) → produção. Um slug de tenant com
 * `pr-` (ex.: `restaurante-pr-grill.console.pratzu.com`) ou um alias de
 * preview de produção do Pages com `pr-` no branch NÃO vão ao staging —
 * caem na API de produção, como antes da #435.
 */
export function resolveAdminApiUrl(host: string): string {
  const pr = prEnvApiUrl(host)
  if (pr) return pr
  if (host.toLowerCase().includes('staging')) return sharedStagingApiUrl(host)
  return ADMIN_API_URL_PRODUCTION
}

/**
 * Ambientes de deploy das allowlists (#406). Só `production` usa a lista
 * restrita; `staging` / `development` / `test` partilham a lista alargada.
 */
export type DeployEnv = 'production' | 'staging' | 'development' | 'test'

/**
 * pratzu.com produção: lista EXPLÍCITA (QA #281), sem curinga nem sufixo —
 * `evil.pratzu.com` NÃO é origem. A única variável é o slug do restaurante,
 * e só sob `menu.pratzu.com` (`<slug>.menu.pratzu.com`, um rótulo).
 */
export const PRATZU_FIRST_PARTY_HOSTS_PRODUCTION = [
  'pratzu.com',
  'www.pratzu.com',
  'console.pratzu.com',
] as const

/** Hosts pratzu.com só fora de produção (#406). */
export const PRATZU_FIRST_PARTY_HOSTS_STAGING = [
  'console-staging.pratzu.com',
  'menu-staging.pratzu.com',
  'landing-staging.pratzu.com',
] as const

/**
 * asknamba.com produção (transição, #282): lista EXPLÍCITA — `evil.asknamba.com`
 * NÃO é origem. Ficam até à fase 3 da migração (decisão PO #406 P3).
 */
export const ASKNAMBA_FIRST_PARTY_HOSTS_PRODUCTION = [
  'asknamba.com',
  'www.asknamba.com',
  'plate.asknamba.com',
  'admin.asknamba.com',
] as const

/** Hosts asknamba.com só fora de produção (#406). */
export const ASKNAMBA_FIRST_PARTY_HOSTS_STAGING = [
  'admin-staging.asknamba.com',
  'menu-staging.asknamba.com',
  'landing-staging.asknamba.com',
] as const

/** slug de restaurante como rótulo DNS (1–63, sem hífen nas pontas) */
const MENU_LABEL = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?'
/**
 * self-restaurant.app (legado dos QR impressos): só `<slug>.menu.` e `admin.`.
 * Esses hosts hoje só redirecionam; mantidos para não quebrar QR antigos.
 */
const LEGACY_HOST = `(?:admin\\.self-restaurant\\.app|${MENU_LABEL}\\.menu\\.self-restaurant\\.app)`

function firstPartyHostRe(pratzu: readonly string[], asknamba: readonly string[]): string {
  const pratzuHost = `(?:${pratzu.map(esc).join('|')}|${MENU_LABEL}\\.menu\\.pratzu\\.com)`
  const asknambaHost = `(?:${asknamba.map(esc).join('|')}|${MENU_LABEL}\\.menu\\.asknamba\\.com)`
  return `(?:${pratzuHost}|${asknambaHost}|${LEGACY_HOST})`
}

const PROD_HOST = firstPartyHostRe(
  PRATZU_FIRST_PARTY_HOSTS_PRODUCTION,
  ASKNAMBA_FIRST_PARTY_HOSTS_PRODUCTION,
)
const NON_PROD_HOST = firstPartyHostRe(
  [...PRATZU_FIRST_PARTY_HOSTS_PRODUCTION, ...PRATZU_FIRST_PARTY_HOSTS_STAGING],
  [...ASKNAMBA_FIRST_PARTY_HOSTS_PRODUCTION, ...ASKNAMBA_FIRST_PARTY_HOSTS_STAGING],
)

/** Path opcional em return_to: sem query nem fragmento. */
const RETURN_PATH = '(\\/[^\\s?#]*)?'

/**
 * Extras só fora de produção: localhost, previews Pages (projeto prod + staging),
 * workers.dev de staging, e pr-N (#435 / PR #456). Pré-compilados ao carregar.
 */
const NON_PROD_ORIGIN_EXTRAS: RegExp[] = [
  /^http:\/\/localhost(:\d+)?$/,
  // Pages do projeto de produção (apex + preview) — só fora de produção (#406).
  /^https:\/\/self-restaurant-admin\.pages\.dev$/,
  /^https:\/\/[\w-]+\.self-restaurant-admin\.pages\.dev$/,
  /^https:\/\/([\w-]+\.)?self-restaurant-admin-staging\.pages\.dev$/,
  /^https:\/\/[\w-]+-staging\.eduardo-044\.workers\.dev$/,
  ...PR_ENV_ORIGIN_PATTERNS,
]

const NON_PROD_RETURN_TO_EXTRAS: RegExp[] = [
  /^http:\/\/localhost(:\d+)?(\/[^\s?#]*)?$/,
  /^https:\/\/([\w-]+\.)?self-restaurant-admin\.pages\.dev(\/[^\s?#]*)?$/,
  /^https:\/\/([\w-]+\.)?self-restaurant-admin-staging\.pages\.dev(\/[^\s?#]*)?$/,
  /^https:\/\/[\w-]+-staging\.eduardo-044\.workers\.dev(\/[^\s?#]*)?$/,
  /^https:\/\/pr-[1-9][0-9]{0,4}-(console|landing)-staging\.pratzu\.com(\/[^\s?#]*)?$/,
  /^https:\/\/([\w-]+\.)?pr-[1-9][0-9]{0,4}-admin-staging\.pages\.dev(\/[^\s?#]*)?$/,
]

/** Regex pré-compiladas — nunca criar RegExp por pedido (#406). */
const ORIGIN_PATTERNS_PRODUCTION: RegExp[] = [new RegExp(`^https:\\/\\/${PROD_HOST}$`)]
const RETURN_TO_PATTERNS_PRODUCTION: RegExp[] = [
  new RegExp(`^https:\\/\\/${PROD_HOST}${RETURN_PATH}$`),
]
const ORIGIN_PATTERNS_NON_PRODUCTION: RegExp[] = [
  new RegExp(`^https:\\/\\/${NON_PROD_HOST}$`),
  ...NON_PROD_ORIGIN_EXTRAS,
]
const RETURN_TO_PATTERNS_NON_PRODUCTION: RegExp[] = [
  new RegExp(`^https:\\/\\/${NON_PROD_HOST}${RETURN_PATH}$`),
  ...NON_PROD_RETURN_TO_EXTRAS,
]

/** Padrões CORS de primeira parte para o ambiente. */
export function firstPartyOriginPatterns(env: DeployEnv): RegExp[] {
  return env === 'production' ? ORIGIN_PATTERNS_PRODUCTION : ORIGIN_PATTERNS_NON_PRODUCTION
}

/**
 * Padrões de return_to (Google SSO / anti open-redirect) para o ambiente:
 * mesmos hosts do CORS, com path opcional e sem query/fragmento.
 */
export function firstPartyReturnToPatterns(env: DeployEnv): RegExp[] {
  return env === 'production' ? RETURN_TO_PATTERNS_PRODUCTION : RETURN_TO_PATTERNS_NON_PRODUCTION
}

/** true se a origem é de primeira parte no ambiente dado. */
export function isFirstPartyOrigin(origin: string, env: DeployEnv): boolean {
  return firstPartyOriginPatterns(env).some((re) => re.test(origin))
}

/** true se a URL de retorno é de primeira parte no ambiente dado. */
export function isFirstPartyReturnTo(url: string, env: DeployEnv): boolean {
  return firstPartyReturnToPatterns(env).some((re) => re.test(url))
}

/** Prefixo de caminho dos cardápios no host da landing (#285). */
export const MENU_PATH_PREFIX = '/r'

/**
 * Slug de restaurante aceito no caminho `/r/<slug>`: mesmo formato de um
 * rótulo DNS (o slug também foi subdomínio): minúsculas, dígitos e hífen, sem
 * hífen nas pontas, 1–63 caracteres. Rejeita `..`, `%2F`, `_a` (assets) etc.
 */
export const MENU_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

/**
 * URL do cardápio de um restaurante (link e QR de mesa), a partir de
 * `CF_MENU_DOMAIN` do ambiente:
 * - base com caminho (`pratzu.com/r`, `landing-staging.pratzu.com/r`, #285) →
 *   `https://pratzu.com/r/<slug><path>` — formato atual;
 * - `menu.<domínio>` (legado, rota curinga) → `https://<slug>.menu.<domínio><path>`;
 * - host único legado (`menu-staging.<domínio>`) → `...<path>?tenant=<slug>`.
 */
export function menuUrl(menuDomain: string, slug: string, path = ''): string {
  const d = menuDomain.toLowerCase().replace(/\/+$/, '')
  const p = path && !path.startsWith('/') ? `/${path}` : path
  if (d.includes('/')) return `https://${d}/${encodeURIComponent(slug)}${p}`
  if (d.startsWith('menu.')) return `https://${slug}.${d}${p}`
  return `https://${d}${p || '/'}?tenant=${encodeURIComponent(slug)}`
}

/** URL do QR de uma mesa: `<cardápio>/table/<qr_token>`. */
export function tableMenuUrl(menuDomain: string, slug: string, qrToken: string): string {
  return menuUrl(menuDomain, slug, `/table/${encodeURIComponent(qrToken)}`)
}

/** Base dos menus de staging: `landing-staging.pratzu.com/r` (#285). */
export const STAGING_MENU_DOMAIN = `landing-staging.${CANONICAL_DOMAIN}${MENU_PATH_PREFIX}`

/**
 * `/r/<slug>[/resto]` → `{ slug, rest }` (`rest` começa com `/` ou é `''`).
 * null se o caminho não for de cardápio ou o slug for inválido.
 */
export function tenantSlugFromMenuPath(pathname: string): { slug: string; rest: string } | null {
  const m = /^\/r\/([^/]+)(\/.*)?$/.exec(pathname)
  if (!m) return null
  let slug: string
  try {
    slug = decodeURIComponent(m[1]!)
  } catch {
    return null
  }
  if (!MENU_SLUG_RE.test(slug)) return null
  return { slug, rest: m[2] ?? '' }
}

/** Base pública de um cardápio: `/r/<slug>`. */
export function menuBasePath(slug: string): string {
  return `${MENU_PATH_PREFIX}/${slug}`
}

/**
 * #238 PR-1 — rótulo dos hosts de QA dos QR legados em staging:
 * `<slug>.menu-staging.{pratzu.com,asknamba.com}` (Custom Domains do Worker
 * `self-restaurant-menu-staging`). O rótulo é `menu-staging` — e NÃO `menu` —
 * para nunca colidir com a rota curinga de produção `*.menu.pratzu.com/*`:
 * um host de QA desses só pode ser atendido pelo Worker de staging.
 */
export const LEGACY_QA_MENU_LABEL = 'menu-staging'

/** Regex pré-compilada (#406): `<slug>.menu-staging.{pratzu,asknamba}.com` → slug. */
const QA_MENU_HOST_RE = new RegExp(`^(${MENU_LABEL})\\.menu-staging\\.(?:pratzu|asknamba)\\.com$`)

/** Host de QA dos QR legados (#238) → slug; null se o host não casar. */
export function qaMenuSlugFromHost(host: string): string | null {
  return QA_MENU_HOST_RE.exec(host.toLowerCase())?.[1] ?? null
}

/**
 * 301 dos hosts antigos de cardápio para `/r/<slug>` (#285), preservando
 * caminho e query:
 * - `<slug>.menu.{asknamba.com,pratzu.com,self-restaurant.app}` →
 *   `${canonicalOrigin}/r/<slug><path>?<query>` — padrão `https://pratzu.com`
 *   (produção); em staging o build passa `PUBLIC_MENU_CANONICAL_ORIGIN`
 *   (`https://landing-staging.pratzu.com`, #238 PR-1) para o 301 nunca cair
 *   na produção;
 * - `menu-staging.{pratzu.com,asknamba.com}` (`?tenant=<slug>`) →
 *   `https://landing-staging.pratzu.com/r/<slug><path>?<query sem tenant>`;
 *   sem `?tenant=` válido → raiz da landing de staging;
 * - `<slug>.menu-staging.{pratzu.com,asknamba.com}` (hosts de QA, #238) →
 *   `https://landing-staging.pratzu.com/r/<slug><path>?<query>` — sempre a
 *   landing de STAGING, independentemente de `canonicalOrigin` (o host já é
 *   de staging por construção).
 * null para qualquer outro host (localhost, workers.dev, pratzu.com/r/...).
 */
export function legacyMenuRedirect(
  requestUrl: string,
  canonicalOrigin: string = CANONICAL_URLS.landing,
): string | null {
  const url = new URL(requestUrl)
  const host = url.hostname.toLowerCase()
  const path = url.pathname === '/' ? '' : url.pathname
  if (/^menu-staging\.(pratzu|asknamba)\.com$/.test(host)) {
    const base = `https://${STAGING_HOSTS.landing}`
    const slug = url.searchParams.get('tenant') ?? ''
    if (!MENU_SLUG_RE.test(slug)) return `${base}/`
    url.searchParams.delete('tenant')
    return `${base}${menuBasePath(slug)}${path}${url.search}`
  }
  const qaSlug = qaMenuSlugFromHost(host)
  if (qaSlug) {
    return `https://${STAGING_HOSTS.landing}${menuBasePath(qaSlug)}${path}${url.search}`
  }
  const slug = tenantSlugFromHost(host, 'menu')
  if (!slug || !MENU_SLUG_RE.test(slug)) return null
  return `${canonicalOrigin}${menuBasePath(slug)}${path}${url.search}`
}

/**
 * Ambiente do Worker do cardápio para o header `X-Pratzu-Env` (#238 PR-1,
 * revisão 3 §4): o E2E noturno dos QR legados FALHA se a resposta do host de
 * QA não trouxer `staging` — só o valor literal `staging` da var de build é
 * aceito; QUALQUER outro (ausente, vazio, capitalização errada) vira
 * `production` (fail-closed: faltar a var no build de staging é falha
 * visível, nunca falso verde).
 */
export type PratzuEnv = 'staging' | 'production'

/** Var de build (`PUBLIC_PRATZU_ENV`) → valor do header `X-Pratzu-Env`. */
export function pratzuEnvFromBuildVar(value: string | undefined): PratzuEnv {
  return value === 'staging' ? 'staging' : 'production'
}

/** Origens da API para connect-src da CSP do cardápio (produção). */
export const API_CONNECT_ORIGINS: string[] = [CANONICAL_DOMAIN, ...TRANSITION_DOMAINS].flatMap(
  (d) => [`https://api.${d}`, `wss://api.${d}`],
)

/** `<slug>.<app>.<domínio servido>` → slug; null se o host não casar. */
export function tenantSlugFromHost(host: string, app: 'menu' | 'admin'): string | null {
  const d = servedDomainOf(host)
  if (!d) return null
  const rest = host
    .toLowerCase()
    .slice(0, -(d.length + 1))
    .split('.')
  // painel: `<slug>.console.pratzu.com` ou `<slug>.admin.<domínio antigo>`
  const label = app === 'admin' ? adminSubdomainFor(d) : app
  return rest.length === 2 && rest[1] === label && rest[0] ? rest[0] : null
}

/** `console.pratzu.com` ou `admin.<domínio antigo>` (painel da plataforma). */
export function isPlatformAdminHost(host: string): boolean {
  const d = servedDomainOf(host)
  return !!d && host.toLowerCase() === `${adminSubdomainFor(d)}.${d}`
}

/**
 * URL da API no MESMO site da página (transição pratzu.com ↔ asknamba.com).
 * O refresh cookie da API é SameSite=Strict e host-only: um painel/cardápio
 * aberto em asknamba.com precisa falar com api(-staging).asknamba.com, e em
 * pratzu.com com api(-staging).pratzu.com. `configured` é a URL do build
 * (padrão: api.pratzu.com); só o domínio é trocado, e só quando ambos (página
 * e API configurada) são domínios de transição/canônico. localhost,
 * workers.dev, pages.dev e self-restaurant.app ficam com `configured`.
 */
export function sameSiteApiUrl(configured: string, pageHost: string | undefined): string {
  if (!pageHost) return configured
  // Domínio pai (não sharedCookieDomain): em staging o cookie é host-only,
  // mas a API ainda tem de acompanhar o site (asknamba ↔ pratzu).
  const pageDomain = cookieParentDomain(pageHost)
  if (!pageDomain) return configured
  let u: URL
  try {
    u = new URL(configured)
  } catch {
    return configured
  }
  const apiDomain = cookieParentDomain(u.hostname)
  if (!apiDomain || apiDomain === pageDomain) return configured
  u.hostname = u.hostname.slice(0, -apiDomain.length) + pageDomain
  return u.toString().replace(/\/$/, '')
}

// ─── Slug do restaurante (self-serve, cardápio em /r/<slug>) ─────────────────

/**
 * Slugs reservados: hosts e caminhos da plataforma (pratzu.com/<x>,
 * <x>.pratzu.com) e nomes que confundiriam o cliente. Nunca viram restaurante.
 */
export const RESERVED_SLUGS: readonly string[] = [
  'www',
  'console',
  'api',
  'admin',
  'app',
  'apps',
  'r',
  'static',
  'assets',
  'asset',
  'sobre',
  'about',
  'landing',
  'staging',
  'menu',
  'menus',
  'mail',
  'email',
  'smtp',
  'imap',
  'pop',
  'ftp',
  'cdn',
  'media',
  'img',
  'images',
  'files',
  'docs',
  'blog',
  'help',
  'ajuda',
  'support',
  'suporte',
  'status',
  'login',
  'logout',
  'signup',
  'register',
  'auth',
  'oauth',
  'account',
  'conta',
  'billing',
  'pagamento',
  'checkout',
  'contratar',
  'restaurantes',
  'plate',
  'platform',
  'plataforma',
  'dashboard',
  'root',
  'system',
  'security',
  'dev',
  'test',
  'demo',
  'preview',
  'beta',
  'internal',
  'localhost',
  'webhook',
  'webhooks',
  'realtime',
  'ws',
  'socket',
  'pratzu',
  'asknamba',
  'namba',
  'tamuu',
  'robots',
  'sitemap',
  'favicon',
  'null',
  'undefined',
  'new',
  'edit',
]

/**
 * #238 PR-1 — slugs de QA reservados SÓ na PRODUÇÃO (bloqueio na criação de
 * tenants, `checkSlug`): o slug de QA dos QR legados tem de existir no
 * staging (seed) e ficar livre lá — por isso NÃO entra em `RESERVED_SLUGS`
 * (isso daria 404 no cardápio de staging). Em produção nenhum restaurante
 * real pode tomá-lo: o slug só existe nos hosts de QA `qa-legado.menu-
 * staging.*`; sem tenant em produção, um QR de QA que escapasse para o
 * Worker de produção acaba em 404 (fail-closed).
 */
export const PRODUCTION_RESERVED_SLUGS: readonly string[] = ['qa-legado']

/** Mínimo de 3 caracteres para slug escolhido (o formato segue MENU_SLUG_RE). */
export const TENANT_SLUG_MIN = 3
export const TENANT_SLUG_MAX = 40

export type SlugProblem = 'format' | 'reserved'

/** true se o slug é reservado da plataforma (case-insensitive). */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug.trim().toLowerCase())
}

/** true se o slug é reservado SÓ no ambiente de produção (#238). */
export function isProductionReservedSlug(slug: string, env: DeployEnv): boolean {
  return env === 'production' && PRODUCTION_RESERVED_SLUGS.includes(slug.trim().toLowerCase())
}

/** null = válido; senão o motivo. */
export function validateTenantSlug(slug: string): SlugProblem | null {
  if (slug.startsWith('xn--')) return 'reserved' // punycode: confunde o cliente
  if (slug.length < TENANT_SLUG_MIN || slug.length > TENANT_SLUG_MAX) return 'format'
  if (!MENU_SLUG_RE.test(slug) || slug.includes('--')) return 'format'
  if (isReservedSlug(slug)) return 'reserved'
  return null
}

/**
 * Candidatos de sugestão para um slug recusado — base normalizada e sufixos
 * -2..-9, SEM olhar o banco. O chamador (API com D1, painel offline) filtra
 * o primeiro válido/livre. A API acrescenta o fallback de sufixo aleatório.
 */
export function suggestSlugCandidates(wanted: string): string[] {
  const base = (validateTenantSlug(wanted) === 'format' ? slugifyName(wanted) : wanted)
    .slice(0, TENANT_SLUG_MAX - 5)
    .replace(/-+$/g, '')
  return [base, ...[2, 3, 4, 5, 6, 7, 8, 9].map((n) => `${base}-${n}`)]
}

/** Nome livre → slug candidato (sem acento, minúsculo, hífens). */
export function slugifyName(name: string): string {
  const s = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '')
  if (!s) return 'restaurante'
  return validateTenantSlug(s) === 'reserved'
    ? `${s}-restaurante`
    : s.length < TENANT_SLUG_MIN
      ? `${s}-restaurante`
      : s
}
